import endpoints from '@/utils/endpoints-constants';
import { lbApiBeta } from '@/utils/axiosConfig';
import i18n, { routeLocale } from '@/i18n';
import axios from 'axios';
import { currentCountry } from '@/plugins/country/module';
import { getCoverageType } from '@/models/search-filter';

export function getGeolocation(geolocationId) {
  try {
    return axios.get(
      `${i18n.t('url_geolocation', routeLocale)}/location/${geolocationId}`
    );
  } catch (e) {
    console.error(e);
  }
}

export function getGeolocationsFromIds(geolocationIds) {
  try {
    return axios.post(`${i18n.t('url_geolocation', routeLocale)}/search/ids`, {
      ids: geolocationIds
    });
  } catch (e) {
    console.error(e);
  }
}

export function searchGeolocation(searchTerm) {
  // The geo service answers `/search/` with 404, so a blank term (the
  // autocomplete firing after the input was cleared) is answered here.
  const term = (searchTerm || '').trim();
  if (!term) {
    return Promise.resolve({ data: [] });
  }

  try {
    return axios.get(
      `${i18n.t('url_geolocation', routeLocale)}/search/${encodeURI(term)}`
    );
  } catch (e) {
    console.error(e);
  }
}

export function searchMapboxAddress(searchTerm, country = currentCountry) {
  return axios.get(
    `https://api.maptiler.com/geocoding/${encodeURI(
      searchTerm
    )}json?language=${country}&key=jTrvi1p07Kys0YeUMuBV'`
  );
}

// A wish search on a locality covers the localities the geo service groups
// with it (a municipality's districts, say), since wishes are stored per
// locality. Returns null when the geo service is unavailable.
export async function getRelatedLocalities(localityId) {
  try {
    const result = await axios.get(
      `${i18n.t('url_geolocation', routeLocale)}/search/relatives/${localityId}`
    );

    return result.data;
  } catch (error) {
    // The caller falls back to the picked locality alone.
  }

  return null;
}

export function addCoverage(coverage) {
  return lbApiBeta.post(endpoints.API.SEARCH_COVERAGES, coverage);
}

export function removeCoverage(coverageId) {
  return lbApiBeta.delete(`${endpoints.API.SEARCH_COVERAGES}/${coverageId}`);
}

export function getCoverageData() {
  return lbApiBeta.get(endpoints.API.SEARCH_COVERAGES);
}

export function getSearchPropositions(searchObject) {
  return lbApiBeta.post(endpoints.API.SEARCH_SEARCH, searchObject);
}

export function getSearchProposition(propositionId) {
  return lbApiBeta.get(`${endpoints.API.SEARCH_PROPOSITION}/${propositionId}`);
}

export function mergeData(oldData, newData) {
  return {
    ...oldData,
    meta: newData.meta,
    results: [...(oldData.results || []), ...(newData.results || [])]
  };
}

// A coverage keeps its filter under `residence` or `wish` depending on the
// search type it was saved in; the geolocation ids live in whichever it is.
function getCoverageBody(coverage) {
  return coverage.body[getCoverageType(coverage)];
}

export async function getCoverageGeolocations(coverages) {
  const geolocationIds = coverages
    .map(x => {
      const body = getCoverageBody(x);
      return body && body.geolocations;
    })
    .filter(x => x)
    .flat();

  const geolocationResult = await getGeolocationsFromIds(geolocationIds);
  if (geolocationResult.data && geolocationResult.data.length > 0) {
    const geolocations = geolocationResult.data;

    for (const coverage of coverages) {
      const body = getCoverageBody(coverage);
      const coverageGeolocationIds = body && body.geolocations;

      if (coverageGeolocationIds && coverageGeolocationIds.length > 0) {
        body.geolocations = geolocations.filter(geolocation =>
          coverageGeolocationIds.some(id => geolocation.id === id)
        );
      }
    }
  }

  return coverages;
}
